"use client";

import React, { useEffect } from "react";
import { getAppLocale } from "../../utils/locale-client";
import AppProviders from "../../providers/app-provider";
import SentryErrorBoundary from "../../components/Common/SentryErrorBoundary";
import { AppLoadingScreen } from "../../components/AppLoadingScreen";
import { useTranslationMessages } from "../../hooks/use-translation-messages";

interface RootLayoutProps {
  children: React.ReactNode;
}

const RootLayout: React.FC<RootLayoutProps> = ({ children }) => {
  // Get locale from cookie (client-side)
  const locale = getAppLocale();

  // Render local messages immediately while remote overrides load in the background.
  const { data: translationMessages, isLoading: translationLoading } =
    useTranslationMessages({ locale });

  // Initialize Google Analytics
  useEffect(() => {
    const gaId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID ?? "G-0000000000";

    if (gaId && typeof window !== "undefined" && !(window as any).dataLayer) {
      // Load gtag script
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(script);

      // Initialize dataLayer
      if (!(window as any).dataLayer) {
        (window as any).dataLayer = [];
      }
      function gtag(...args: any[]): void {
        (window as any).dataLayer?.push(args);
      }
      gtag("js", new Date());
      gtag("config", gaId);
    }
  }, []);

  // Set document locale and class dynamically
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
      document.documentElement.className = "light";
      document.body.className = "font-mulish";
    }
  }, [locale]);

  if (translationLoading) {
    return <AppLoadingScreen />;
  }

  return (
    <AppProviders
      locale={locale}
      translationMessages={translationMessages ?? {}}
    >
      <SentryErrorBoundary>{children}</SentryErrorBoundary>
    </AppProviders>
  );
};

export default RootLayout;
